/**
 * Chat API Service
 *
 * API client functions for conversation chat with AI therapist
 * including creating chats, sending messages, and managing chat history.
 */

import { apiClient, getCurrentAccessToken } from "./api";
import type {
  ChatMessageCreate,
  ChatMessageResponse,
  ConversationChatCreate,
  ConversationChatResponse,
  SendMessageResponse,
} from "@/types/chat";

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8101";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Create a new chat session or get existing one for a reflection
 */
export async function createOrGetChat(
  reflectionId: string,
  request?: ConversationChatCreate
): Promise<ConversationChatResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/reflections/${reflectionId}/chat`,
    request || {}
  );
  return response.data;
}

/**
 * Get existing chat session for a reflection
 */
export async function getChat(
  reflectionId: string
): Promise<ConversationChatResponse> {
  const response = await apiClient.get(
    `${API_PREFIX}/reflections/${reflectionId}/chat`
  );
  return response.data;
}

/**
 * Send a message in a chat and receive AI response
 */
export async function sendMessage(
  reflectionId: string,
  message: ChatMessageCreate
): Promise<SendMessageResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/reflections/${reflectionId}/chat/messages`,
    message
  );
  return response.data;
}

/**
 * Delete chat session and all messages for a reflection
 */
export async function deleteChat(reflectionId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/reflections/${reflectionId}/chat`);
}

/**
 * Callback types for streaming message events
 */
export interface StreamingCallbacks {
  onUserMessage?: (message: ChatMessageResponse) => void;
  onContent?: (content: string) => void;
  onDone?: (message: ChatMessageResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Send a message with streaming response
 *
 * Uses Server-Sent Events (SSE) to stream the AI response in real-time.
 */
export async function sendMessageStreaming(
  reflectionId: string,
  message: ChatMessageCreate,
  callbacks: StreamingCallbacks
): Promise<void> {
  // Get the auth token
  const token = await getCurrentAccessToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(
    `${API_BASE_URL}${API_PREFIX}/reflections/${reflectionId}/chat/messages/stream`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(message),
      credentials: "include",
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to send message: ${errorText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  // Helper function to process SSE events from lines
  const processSSELines = (lines: string[]) => {
    let currentEvent = "";
    let currentData = "";

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7);
      } else if (line.startsWith("data: ")) {
        currentData = line.slice(6);
      } else if (line === "" && currentEvent && currentData) {
        // End of event, process it
        try {
          const data = JSON.parse(currentData);

          switch (currentEvent) {
            case "user_message":
              callbacks.onUserMessage?.(data);
              break;
            case "content":
              callbacks.onContent?.(data.content);
              break;
            case "done":
              callbacks.onDone?.(data);
              break;
            case "error":
              callbacks.onError?.(data.error);
              break;
          }
        } catch (e) {
          console.error("Failed to parse SSE data:", e);
        }

        currentEvent = "";
        currentData = "";
      }
    }
  };

  try {
    let reading = true;
    while (reading) {
      const { done, value } = await reader.read();

      if (done) {
        reading = false;
        // Don't continue - we still need to process any remaining buffer below
      }

      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      // Process complete SSE events from buffer
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep incomplete line in buffer

      processSSELines(lines);
    }

    // Process any remaining buffer after stream ends
    // This handles the case where the final "done" event wasn't yet processed
    if (buffer) {
      // Add empty string to trigger processing of any complete event in buffer
      const finalLines = buffer.split("\n");
      finalLines.push(""); // Ensure we process the last event
      processSSELines(finalLines);
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Generate a summary/feedback from a journal reflection's chat conversation
 *
 * Queues a background task to generate AI feedback based on the chat
 * conversation history. Only valid for journal-type reflections.
 */
export async function generateChatSummary(
  reflectionId: string
): Promise<{ status: string; message: string; reflection_id: string }> {
  const response = await apiClient.post(
    `${API_PREFIX}/reflections/${reflectionId}/chat/generate-summary`
  );
  return response.data;
}
